import React, { forwardRef, LegacyRef } from 'react';
import { Typography } from 'fronton-react';
import { getDisableProductComparisons } from 'common/selectors';
import { cn } from 'utils/classnames';
import { Subtitle } from '../subtitle';
import { ProductReviews } from './product-reviews';
import { ProductCartButton } from './product-cart-button';
import { ProductCardProps } from './types';
import { ProductName } from './product-name';
import {
  productWrapper,
  offerWrapper,
  productNameText,
  productName,
  productId,
  contentWrapper,
  offerOldPriceWrapper,
  productNameTextInWidget,
  onlineOrderOnlyClassName,
} from './styles';
import { subtitleStyles } from '../subtitle/styles';
import { cartButton, compareListActionWrapper } from './common-styles';
import { ProductPriceToDisplay } from './product-price-to-display';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getIsBot } from 'common/selectors/is-bot';
import { isOnlineOnly } from 'common/utils/analytics';
import { CompareListAction } from 'features/products-compare/components';
import { OnlineOrderOnly } from '@navi-toolbox/ui-components';
import { SET_PRODUCT_CATEGORY } from '../../constants';
import { useProductCard } from './hooks/use-product-card';
import { ProductImageLink } from './product-image-link';
import { ProductActionButtons } from './product-action-buttons';

export const DATA_QA_ONLINE_ONLY = 'online-order-only';
export const DATA_QA_PRODUCT = 'product';
export const DATA_QA_PRODUCT_NAME = 'product-name';
export const DATA_QA_PRODUCT_REVIEWS = 'product-reviews';

export const ProductCard = forwardRef<HTMLDivElement, ProductCardProps>(
  (
    {
      className,
      product,
      subtitle,
      productPosition,
      type,
      label,
      inBasket,
      isBasketLoading,
      qty,
      onClick,
      onCartAdd,
      onCartRemove,
      reviews,
      onQtyUpdate,
      onShoplistToggle,
      inWidget,
      inShoplist,
      elementBlock = 'not set',
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const isOnlyOrderOnline = isOnlineOnly(label);
    const isBot = useSelector(getIsBot);
    const disableComparisons = useSelector(getDisableProductComparisons);
    const isSet = product.category === SET_PRODUCT_CATEGORY;

    const {
      handleAdd,
      handleRemove,
      handleUpdate,
      hasOldPrice,
      link,
      loyaltyRewardLabel,
      reviewsLink,
      setShowMeasurementTip,
      showMeasurementTip,
    } = useProductCard({
      product,
      onCartAdd,
      onCartRemove,
      onQtyUpdate,
    });

    return (
      <div
        className={cn(productWrapper, type, className)}
        data-product-item=""
        data-qa-product=""
        data-qa={DATA_QA_PRODUCT}
        opp-data-product-id={product.productId}
        ref={ref as LegacyRef<HTMLDivElement>}
      >
        <ProductImageLink
          link={link}
          product={product}
          onClick={onClick}
          type={type}
          label={label}
          inWidget={inWidget}
          loyaltyRewardLabel={loyaltyRewardLabel}
        />
        <div className={cn(contentWrapper, type)}>
          <div className={subtitleStyles}>
            {subtitle && product.monetization ? (
              <Subtitle subtitle={subtitle} />
            ) : (
              <Typography
                variant="caption"
                color="text-minor"
                className={productId}
                data-qa="product-article"
              >
                {t('product-card.product-id')} {product.productId}
              </Typography>
            )}
          </div>

          <ProductName
            data-qa={DATA_QA_PRODUCT_NAME}
            href={link}
            onClick={() => onClick('productNameLink')}
            className={productName}
            textClassName={inWidget ? productNameTextInWidget : productNameText}
          >
            {product.displayedName}
          </ProductName>
          {reviews && reviews.reviews_number > 0 && !isSet && (
            <ProductReviews
              data-qa={DATA_QA_PRODUCT_REVIEWS}
              href={reviewsLink}
              rating={reviews.rating}
              reviewsNumber={reviews.reviews_number}
              onClick={() => onClick('productRatingLink')}
            />
          )}
        </div>
        <div className={cn(offerWrapper, type, hasOldPrice ? offerOldPriceWrapper : '')}>
          <ProductPriceToDisplay
            hasOldPrice={hasOldPrice}
            price={product.price}
            productPriceCategory={label}
          />
          <ProductActionButtons
            showMeasurementTip={showMeasurementTip}
            setShowMeasurementTip={setShowMeasurementTip}
            qty={qty}
            product={product}
            inBasket={inBasket}
            inShoplist={inShoplist}
            onShoplistToggle={onShoplistToggle}
            handleRemove={handleRemove}
            handleUpdate={handleUpdate}
            isSet={isSet}
          >
            <ProductCartButton
              className={cartButton}
              isActive={inBasket}
              isLoading={isBasketLoading}
              isBot={isBot}
              onClick={handleAdd}
            />
          </ProductActionButtons>

          <OnlineOrderOnly
            text={t('product-card.online-order-only')}
            visible={!isBot && isOnlyOrderOnline}
            className={onlineOrderOnlyClassName}
            data-qa={DATA_QA_ONLINE_ONLY}
          />
        </div>

        {!disableComparisons && !isSet && (
          <div className={compareListActionWrapper}>
            <CompareListAction
              hideText
              product={product}
              productPosition={productPosition}
              elementBlock={elementBlock}
            />
          </div>
        )}
      </div>
    );
  },
);
